import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Button } from "@/components/ui/button";
import { FileText, CheckCircle2, Repeat, ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";

const QUOTE_STATUS = [
  { value: "draft", label: "Luonnos" },
  { value: "sent", label: "Lähetetty" },
  { value: "accepted", label: "Hyväksytty" },
  { value: "rejected", label: "Hylätty" },
  { value: "expired", label: "Vanhentunut" },
  { value: "converted", label: "Muunnettu laskuksi" },
];
const INTERVALS = [
  { value: "weekly", label: "Viikoittain" },
  { value: "monthly", label: "Kuukausittain" },
  { value: "quarterly", label: "Neljännesvuosittain" },
  { value: "yearly", label: "Vuosittain" },
];

export default function QuotesDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { api.get("/quotes/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const convertQuote = async (quote_id, num) => {
    if (!window.confirm(`Muunnetaanko tarjous #${num} laskuksi?`)) return;
    try {
      const r = await api.post(`/quotes/${quote_id}/convert-to-invoice`);
      toast.success(`Lasku #${r.data.invoice_number} luotu tarjouksesta`);
      navigate(`/invoicing/${r.data.invoice_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateRecurring = async (rec_id, name) => {
    if (!window.confirm(`Luo lasku toistuvasta "${name}"?`)) return;
    try {
      const r = await api.post(`/quotes/recurring-invoices/${rec_id}/generate-now`);
      toast.success(`Lasku #${r.data.invoice_number} luotu`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quotes = {
    endpoint: "/quotes/items",
    testidPrefix: "quotes",
    emptyMessage: "Ei tarjouksia",
    defaults: { recipient_name: "", quote_date: new Date().toISOString().slice(0, 10), valid_until: "", status: "draft", items: [] },
    fields: [
      { key: "recipient_name", label: "Asiakas", type: "text", required: true },
      { key: "recipient_email", label: "Sähköposti", type: "text" },
      { key: "recipient_business_id", label: "Y-tunnus", type: "text" },
      { key: "recipient_address", label: "Osoite", type: "text", span: 2 },
      { key: "quote_date", label: "Tarjouksen pvm", type: "date" },
      { key: "valid_until", label: "Voimassa asti", type: "date" },
      { key: "status", label: "Tila", type: "select", options: QUOTE_STATUS },
      { key: "notes", label: "Lisätiedot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "quote_number", label: "Nro" },
      { key: "recipient_name", label: "Asiakas" },
      { key: "quote_date", label: "Pvm" },
      { key: "valid_until", label: "Voimassa" },
      { key: "total_amount", label: "Yhteensä", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
    actions: (it) =>
      it.status !== "converted" ? (
        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); convertQuote(it.id, it.quote_number); }} title="Muunna laskuksi" data-testid={`quote-convert-${it.quote_number}`}>
          <ArrowRight className="w-4 h-4 text-emerald-400" />
        </Button>
      ) : null,
  };

  const recurring = {
    endpoint: "/quotes/recurring-invoices/items",
    testidPrefix: "recurring",
    emptyMessage: "Ei toistuvia laskuja",
    defaults: { name: "", is_active: true, interval: "monthly", next_invoice_date: new Date().toISOString().slice(0, 10), recipient_name: "", items: [] },
    fields: [
      { key: "name", label: "Otsikko", type: "text", required: true },
      { key: "recipient_name", label: "Asiakas", type: "text", required: true },
      { key: "recipient_email", label: "Sähköposti", type: "text" },
      { key: "interval", label: "Toistoväli", type: "select", options: INTERVALS, required: true },
      { key: "next_invoice_date", label: "Seuraava laskutuspäivä", type: "date", required: true },
      { key: "is_active", label: "Aktiivinen", type: "boolean" },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "name", label: "Otsikko" },
      { key: "recipient_name", label: "Asiakas" },
      { key: "interval", label: "Väli", format: "badge" },
      { key: "next_invoice_date", label: "Seuraava" },
      { key: "total_amount", label: "Summa", format: "eur", align: "right" },
      { key: "invoices_created", label: "Luotu kpl", align: "right" },
      { key: "is_active", label: "Aktiivinen" },
    ],
    actions: (it) => (
      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); generateRecurring(it.id, it.name); }} title="Luo lasku nyt" data-testid={`recurring-gen-${it.id}`}>
        <Repeat className="w-4 h-4 text-amber-300" />
      </Button>
    ),
  };

  return (
    <div className="space-y-6" data-testid="quotes-dashboard">
      <PageHeader kicker="Sovellus" title="Tarjoukset & Toistuvat laskut" description="Tarjoukset, hyväksyminen ja toistuva laskutus." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<FileText className="w-5 h-5" />} label="Tarjouksia yht." value={stats?.quotes_total ?? 0} loading={loading} testid="q-kpi-total" />
        <KpiCard icon={<CheckCircle2 className="w-5 h-5" />} label="Hyväksytty" value={stats?.accepted ?? 0} accent="text-emerald-300" loading={loading} testid="q-kpi-accepted" />
        <KpiCard icon={<Repeat className="w-5 h-5" />} label="Muunnettu" value={stats?.converted ?? 0} accent="text-amber-300" loading={loading} testid="q-kpi-converted" />
        <KpiCard icon={<FileText className="w-5 h-5" />} label="Avoin arvo" value={formatEur(stats?.open_value)} loading={loading} testid="q-kpi-value" />
      </div>
      <Panel title="Hallinta">
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="quotes" data-testid="q-tab-quotes">Tarjoukset</TabsTrigger>
            <TabsTrigger value="recurring" data-testid="q-tab-recurring">Toistuvat laskut</TabsTrigger>
            <TabsTrigger value="staff" data-testid="q-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="quotes"><CrudList config={quotes} /></TabsContent>
          <TabsContent value="recurring"><CrudList config={recurring} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="quotes" moduleLabel="Tarjoukset" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
